import React from 'react';
import cx from 'clsx';
import './color-swatch.styles.scss';
import {IColor} from "@montana/models/generated/cms.interface";

export type ColorSwatchVariant = 'card' | 'row' | 'color'| 'list';

export interface IColorSwatchProps extends IColor {
    variant?: ColorSwatchVariant;
    label?: string;
    hexColor?: string;
    hoverLabel?: string;
    forceHover?: boolean;
    ariaLabel?: string;
    disableRowHover?: boolean;
    size?: 'small' | 'large';
}

/**
 * Color swatch component
 *
 * @param variant {ColorSwatchVariant} - card, row, color or list. Defaults to card.
 * @param imageSrc {IImage} - image source. If provided, color swatch will be rendered as an image. If not provided, color swatch will be rendered as a fill with the provided color.
 * @param hexColor {string} - hex color code. Required if imageSrc is not provided.
 * @param colorName {string} - color name. Required if imageSrc is not provided.
 * @param surfaceLook {string} - surface type. Required if variant is row or list.
 * @param surfaceFeel {string} - surface type. Required if variant is row or list.
 * @param label {string} - label to be displayed on hover. Defaults to 'Explore Colour'. Only used if imageSrc is not provided and the variant is a card.
 * @param hoverLabel {string} - label to be displayed on hover. Defaults to 'Explore Colour'. Only used if imageSrc is not provided and the variant is not a card.
 * @param forceHover {boolean} - force hover state. Defaults to false.
 * @param ariaLabel {string} - aria label. Defaults to 'Color Swatch'.
 * @constructor
 */
export const ColorSwatch: React.FC<IColorSwatchProps> = ({
    variant = 'card',
    hexColor,
    colorName,
    colorSwatchImage,
    surfaceLook,
    surfaceFeel,
    label,
    hoverLabel,
    forceHover = false,
    ariaLabel,
    disableRowHover = false,
    size,
}) => {
    const isRow = variant === 'row';
    const onlyColor = variant === 'color';
    const isList = variant === 'list';
    const displayLabel = label || colorName || undefined;
    const swatchAriaLabel = ariaLabel || displayLabel || undefined;
    const swatchRole = colorSwatchImage?.url ? undefined : swatchAriaLabel ? 'img' : undefined;
    const swatchLabel = colorSwatchImage?.url ? undefined : swatchAriaLabel;

    const imageScalingQueryParams = ['?mode=crop'];

    const squareSize = variant === 'card' || variant === 'color' ? 400 : 50;

    const hexColorBorder = hexColor ? `1px solid hsl(from #${hexColor} h s calc(l - 10))` : undefined;

    if (variant === 'card' || variant === 'color') {
        imageScalingQueryParams.push(`width=${squareSize}`);
        imageScalingQueryParams.push(`height=${squareSize}`);
    } else {
        imageScalingQueryParams.push(`width=${squareSize}`);
        imageScalingQueryParams.push(`height=${squareSize}`);
    }

    return (
        <div
            className={cx(
                'color-swatch',
                `color-swatch--${variant}`,
                {
                    'color-swatch--hover': forceHover,
                    'color-swatch--disable-color': disableRowHover,
                    'color-swatch--large': size === 'large',
                    'color-swatch--small': size === 'small',
                },
            )}
        >
            <div className="color-swatch__swatch" role={swatchRole} aria-label={swatchLabel}>
                {colorSwatchImage?.url ? (
                    <img className="color-swatch__image" src={colorSwatchImage?.url + imageScalingQueryParams.join('&')} alt={swatchAriaLabel || ''} loading="lazy"/>
                ) : (
                    <div className="color-swatch__fill" style={{backgroundColor: `#${hexColor}`, border: hexColorBorder}}/>
                )}
                {variant === 'card' && hoverLabel && (
                    <div className="color-swatch__overlay" aria-hidden="true">
                        <span className="color-swatch__overlay-text">{hoverLabel}</span>
                    </div>
                )}
            </div>

            {(!onlyColor && !isList) && (

                <div className="color-swatch__meta">
                    {!isRow && colorName && (
                        <div className="color-swatch__title">
                            {colorName && <span className="color-swatch__name">{colorName}</span>}
                        </div>
                    )}
                    {isRow && displayLabel && <span className="color-swatch__label">{displayLabel}</span>}
                    {!isRow && (surfaceLook || surfaceFeel) && <span className="color-swatch__surface-type">{surfaceLook} {surfaceFeel}</span>}
                </div>
            )}
        </div>
    );
};

ColorSwatch.displayName = 'ColorSwatch';
